import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { SignOut, Icon, UserCircle } from "@phosphor-icons/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { AppShell, NotificationCenter, ThemeToggle } from "@void-orbit/ui";
import { useStore } from "../../../store";
import { UserRole } from "../../../models/User";
import { Channel, ChannelStatus, mergeChannelFallback, normalizeChannels } from "../../../models/Channel";
import { Button, Color, Size, Variant } from "../../Buttons";
import { useSocket } from "../../../providers/socket/socketProvider";
import { listChannels } from "../../../services/channelServices";
import { resolveChannelStatusPayload, ChannelStatusPayload } from "../../../controllers/channelRealtime";
import { getPostLoginRedirectPath } from "../../../services/authRedirectServices";
import { isCentralAuthEnabled, logoutAuthenticatedUser, redirectToCentralAuth } from "../../../services/authServices";
import { MessageNotificationListener } from "../../MessageNotificationListener";
import { Notification } from "../../../models/Notification";
import { getNotifications, markNotificationRead, normalizeNotification } from "../../../services/notificationService";
import { getMessageNotificationTypePreferences, requestSystemNotificationPermission, showSystemNotification } from "../../../utils/messageNotifications";
import { getAssignedRole, hasRole, roleGroups, userRoleLabels } from "../../../services/accessControl";
import { buildShellNav, getPageTitle, manageChannelsURL, menuItems, shouldLoadMoreNotifications } from "./navigation";

interface UserMenuItemConfig {
  Icon: Icon;
  label: string;
  onClick: () => void;
  color: Color;
  roles: ReadonlyArray<UserRole>;
};

const notificationsPerPage = 20;

export const AppLayout = () => {
  const navigate = useNavigate();

  const {
    pathname,
    search,
  } = useLocation();

  const {
    themeMode,
    logout,
    changeTheme,
    user,
    changeChannelStatus,
    setChannelConnections,
  } = useStore();

  const {
    socket,
  } = useSocket();

  const [ notifications, setNotifications ] = useState<Array<Notification>>([]);
  const [ unreadNotificationsCount, setUnreadNotificationsCount ] = useState<number>( 0 );
  const [ notificationsPage, setNotificationsPage ] = useState<number>( 1 );
  const [ notificationsTotal, setNotificationsTotal ] = useState<number>( 0 );
  const [ loadingNotifications, setLoadingNotifications ] = useState<boolean>( false );
  const assignedRole = getAssignedRole( user );
  const pageTitle = getPageTitle( pathname, assignedRole );

  useEffect(() => {
    document.title = `${ pageTitle } | Void Chat`;
  }, [ pageTitle ]);

  const systemNotifiedNotificationIdsRef = useRef<Set<string>>( new Set());
  const notificationPermissionRequestedRef = useRef<boolean>( false );
  const currentUserIdRef = useRef<string | undefined>( user?.id );

  useEffect(() => {
    if( pathname !== "/" ) return;

    const redirectPath = getPostLoginRedirectPath( search );

    if( redirectPath ) navigate( redirectPath, { replace: true });
  }, [ navigate, pathname, search ]);

  const channels = useMemo(() => (
    mergeChannelFallback(user?.organization.channelConnections ?? [], user?.organization.channel)
  ), [user?.organization.channel, user?.organization.channelConnections]);
  const disconnectedChannels = useMemo(() => (
    channels.filter((channel) => channel.status !== ChannelStatus.CONNECTED)
  ), [channels]);
  const canManageChannels = assignedRole === UserRole.ADMIN;
  const shouldShowChannelAlert = disconnectedChannels.length > 0;
  const channelAlertMessage = disconnectedChannels.length > 1
    ? `${ disconnectedChannels.length } canais estão desconectados.`
    : "Um canal está desconectado.";
  const manageURL = manageChannelsURL( assignedRole, user?.organization?.id );

  const loadNotifications = async ( page = 1, append = false ) => {
    if( loadingNotifications ) return;

    setLoadingNotifications( true );

    const response = await getNotifications({ page, perPage: notificationsPerPage });
    if( response.data !== false ) {
      const nextNotifications = Array.isArray( response.data.results ) ? response.data.results : [];
      setNotifications(( currentNotifications ) => {
        if( !append ) return nextNotifications;

        const byId = new Map<string, Notification>();
        currentNotifications.forEach(( notification ) => byId.set( notification.id, notification ));
        nextNotifications.forEach(( notification ) => byId.set( notification.id, notification ));
        return Array.from( byId.values());
      });
      setUnreadNotificationsCount( Number( response.data.unreadCount ?? 0 ));
      setNotificationsPage( Number( response.data.page ?? page ));
      setNotificationsTotal( Number( response.data.total ?? nextNotifications.length ));
    }

    setLoadingNotifications( false );
  };

  const handleNotificationClick = async ( notification: Notification ) => {
    if( !notification.readAt ) {
      const response = await markNotificationRead( notification.id );
      if( response.data !== false ) {
        const updatedNotification = response.data;
        setNotifications(( items ) => items.map(( item ) => item.id === updatedNotification.id ? updatedNotification : item ));
        setUnreadNotificationsCount(( count ) => Math.max( 0, count - 1 ));
      }
    }

    if( notification.actionPath ) navigate( notification.actionPath );
  };

  const mergeRealtimeNotifications = ( incomingNotifications: Array<Notification> ) => {
    const currentUserId = currentUserIdRef.current;
    const userNotifications = incomingNotifications
      .map( normalizeNotification )
      .filter(( notification ) => notification.userId === currentUserId );

    if( userNotifications.length === 0 ) return;

    const notificationTypes = getMessageNotificationTypePreferences( currentUserId );
    if( notificationTypes.system ) {
      userNotifications.forEach(( notification ) => {
        if( systemNotifiedNotificationIdsRef.current.has( notification.id )) return;

        systemNotifiedNotificationIdsRef.current.add( notification.id );
        if( systemNotifiedNotificationIdsRef.current.size > 100 ) {
          const oldestNotificationId = systemNotifiedNotificationIdsRef.current.values().next().value;
          if( oldestNotificationId ) systemNotifiedNotificationIdsRef.current.delete( oldestNotificationId );
        }

        showSystemNotification({
          title: notification.title || "Nova notificacao",
          body: notification.body || "Voce tem uma nova notificacao.",
          tag: `notification:${ notification.id }`,
          onClick: () => void handleNotificationClick( notification ),
        });
      });
    }

    setNotifications(( currentNotifications ) => {
      const currentNotificationIds = new Set( currentNotifications.map(( notification ) => notification.id ));
      const unreadNewNotifications = userNotifications.filter(( notification ) => !notification.readAt && !currentNotificationIds.has( notification.id ));

      if( unreadNewNotifications.length > 0 ) {
        setUnreadNotificationsCount(( count ) => count + unreadNewNotifications.length );
        setNotificationsTotal(( total ) => total + unreadNewNotifications.length );
      }

      const byId = new Map<string, Notification>();
      currentNotifications.forEach(( notification ) => byId.set( notification.id, notification ));
      userNotifications.forEach(( notification ) => byId.set( notification.id, notification ));

      return Array.from( byId.values())
        .sort(( firstNotification, secondNotification ) => secondNotification.createdAt.getTime() - firstNotification.createdAt.getTime());
    });
  };

  useEffect(() => {
    currentUserIdRef.current = user?.id;

    if( user?.id ) void loadNotifications();
    // eslint-disable-next-line
  }, [ user?.id ]);

  useEffect(() => {
    const notificationTypes = getMessageNotificationTypePreferences( user?.id );
    if( !notificationTypes.system ) {
      notificationPermissionRequestedRef.current = false;
      return;
    }

    if( notificationPermissionRequestedRef.current ) return;
    notificationPermissionRequestedRef.current = true;

    void requestSystemNotificationPermission();
  }, [ user?.id ]);

  const loadMoreNotifications = () => {
    if( !shouldLoadMoreNotifications( loadingNotifications, notifications.length, notificationsTotal )) return;

    void loadNotifications( notificationsPage + 1, true );
  };

  useEffect(() => {
    if( !user?.organization?.id || !canManageChannels ) return;

    let mounted = true;
    void (async () => {
      const response = await listChannels(undefined);
      if( !mounted || response.data === false ) return;

      setChannelConnections(normalizeChannels(response.data));
    })();

    return () => {
      mounted = false;
    };
  }, [canManageChannels, setChannelConnections, user?.organization?.id]);

  useEffect(() => {
    if ( !socket ) return;

    const handleChannelStatus = (payload: ChannelStatusPayload) => {
      const { status } = resolveChannelStatusPayload(payload);
      if( !status ) return;

      const channel = payload?.channel || {};
      changeChannelStatus({
        ...channel,
        id: channel.id || payload?.sessionId || "",
        organizationId: channel.organizationId || payload?.organizationId,
        status,
        updatedAt: channel.updatedAt ? new Date(channel.updatedAt) : new Date(),
      } as Channel);
    };

    const handleNotificationNew = (payload: { notification?: Notification; notifications?: Array<Notification> }) => {
      const incomingNotifications = Array.isArray( payload?.notifications )
        ? payload.notifications
        : payload?.notification
          ? [ payload.notification ]
          : [];

      mergeRealtimeNotifications( incomingNotifications );
    };

    socket.on("channel:status", handleChannelStatus);
    socket.on("notification:new", handleNotificationNew);

    return () => {
      socket.off("channel:status", handleChannelStatus);
      socket.off("notification:new", handleNotificationNew);
    };
    // eslint-disable-next-line
  }, [ socket ]);

  const userMenuItems: Array<UserMenuItemConfig> = [
    {
      Icon: UserCircle,
      color: "white",
      label: "Perfil",
      onClick: () => {},
      roles: roleGroups.all,
    },
    {
      Icon: SignOut,
      color: "error",
      label: "Sair",
      onClick: async () => {
        await logoutAuthenticatedUser();

        logout();

        if( isCentralAuthEnabled()) {
          redirectToCentralAuth();
          return;
        }

        navigate("/");
      },
      roles: roleGroups.all,
    },
  ];

  return (
    <>
      <MessageNotificationListener />

      <AppShell
        brand={{ name: "Void Chat", product: "chat" }}
        nav={ buildShellNav( menuItems, assignedRole, pathname, navigate ) }
        breadcrumb="Workspace"
        title={ pageTitle }
        focusKey={ pathname }
        padded={ ![( assignedRole !== UserRole.ATTENDANT ? "/" : "")].includes( pathname )}
        actions={
          <>
            <NotificationCenter
              items={ notifications.map(( notification ) => ({
                id: notification.id,
                title: notification.title,
                body: notification.body || undefined,
                meta: notification.createdAt.toLocaleString(),
                read: Boolean( notification.readAt ),
              })) }
              unreadCount={ unreadNotificationsCount }
              loading={ loadingNotifications }
              emptyLabel="Nenhuma notificação de agendamento."
              onItemSelect={( id ) => {
                const notification = notifications.find(( item ) => item.id === id );
                if( notification ) void handleNotificationClick( notification );
              }}
              onEndReached={ loadMoreNotifications }
            />

            <ThemeToggle mode={ themeMode } onToggle={ changeTheme } />
          </>
        }
        user={ user ? {
          initial: user.name.charAt( 0 ).toUpperCase(),
          name: user.name,
          subtitle: assignedRole ? userRoleLabels[ assignedRole ] : undefined,
          organization: user.organization?.name,
          menu: userMenuItems
            .filter(( item ) => hasRole( assignedRole, item.roles ))
            .map(({ label, color, Icon: ItemIcon, onClick }) => ({
              id: label,
              label,
              color,
              icon: <ItemIcon size={ 20 } weight="regular" />,
              onSelect: onClick,
            })),
        } : undefined }
        banner={ shouldShowChannelAlert ? {
          message: channelAlertMessage,
          action: manageURL
            ? (
                <Button
                  $size={ Size.VERY_SMALL }
                  $variant={ Variant.OUTLINED }
                  onClick={() => window.open( manageURL, "_blank", "noopener" )}
                >
                  Gerenciar
                </Button>
              )
            : undefined,
        } : null }
      >
        <Outlet />
      </AppShell>
    </>
  );
};
